import { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Navigation, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Nishant+Tower%2C+MG+Road%2C+Akola%2C+Maharashtra+444001";

const initialForm = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      er.email = "Please enter a valid email address.";
    if (form.phone.trim() && !/^[+\d][\d\s-]{6,18}$/.test(form.phone.trim()))
      er.phone = "Please enter a valid phone number.";
    if (form.subject.trim().length < 2) er.subject = "Please enter a subject.";
    if (form.message.trim().length < 10)
      er.message = "Please write a message of at least 10 characters.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setForm(initialForm);
      if (data.email_sent) {
        toast.success("Message sent", {
          description:
            "Thank you for reaching out. Your message has been delivered to the Nurturing Happiness team.",
        });
      } else {
        toast.success("Message received", {
          description:
            "Your message has been saved. If you need a quicker response, email us directly at nurturinghappiness2@gmail.com.",
        });
      }
    } catch (err) {
      toast.error("Could not send your message", {
        description:
          "Please try again in a moment, or reach us directly at nurturinghappiness2@gmail.com.",
      });
    } finally {
      setSending(false);
    }
  };

  const fieldClass =
    "mt-2 min-h-12 rounded-xl border-nh-deep/15 bg-white px-4 py-3 text-[15px] text-nh-ink placeholder:text-nh-muted/60 focus-visible:ring-nh-green";

  return (
    <>
      <SEO
        title="Contact Nurturing Happiness | Akola"
        description="Have a question, suggestion or want to learn more about Nurturing Happiness? Get in touch with our team in Akola, Maharashtra."
      />

      <section data-testid="contact-header" className="paper-texture py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-nh-green">Contact</p>
            <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-nh-ink sm:text-5xl">
              Let&apos;s Connect
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-nh-muted md:text-lg">
              Have a question, suggestion or want to learn more about Nurturing Happiness?
              We&apos;d love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="contact-info" className="bg-white py-16 md:py-20">
        <div className="mx-auto grid max-w-7xl gap-6 px-6 sm:grid-cols-2 lg:grid-cols-3 md:px-12">
          <Reveal>
            <div className="flex h-full flex-col rounded-3xl border border-nh-deep/5 bg-nh-cream p-8 shadow-soft transition-all duration-300 hover:-translate-y-2 hover:shadow-lift">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-nh-mint text-nh-green">
                <MapPin className="h-7 w-7" strokeWidth={1.75} />
              </div>
              <h2 className="mt-6 font-heading text-xl font-semibold text-nh-ink">📍 Visit Us</h2>
              <p data-testid="contact-address" className="mt-3 text-[15px] leading-relaxed text-nh-muted">
                2nd Floor, Nishant Tower, MG Road
                <br />
                Akola, Maharashtra – 444001
                <br />
                India
              </p>
              <div className="mt-auto pt-7">
                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="get-directions-btn"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-nh-green/25 px-5 py-2 text-sm font-semibold text-nh-green transition-all duration-300 hover:border-nh-green hover:bg-nh-green hover:text-white active:scale-95"
                >
                  <Navigation className="h-4 w-4" /> Get Directions
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex h-full flex-col rounded-3xl border border-nh-deep/5 bg-nh-cream p-8 shadow-soft transition-all duration-300 hover:-translate-y-2 hover:shadow-lift">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-50 text-nh-blue">
                <Phone className="h-7 w-7" strokeWidth={1.75} />
              </div>
              <h2 className="mt-6 font-heading text-xl font-semibold text-nh-ink">📞 Call Us</h2>
              <a
                href="tel:+919921193939"
                data-testid="contact-phone-link"
                className="mt-3 text-[15px] font-medium text-nh-body transition-colors hover:text-nh-green"
              >
                +91 99211 93939
              </a>
              <p className="mt-2 text-sm leading-relaxed text-nh-muted">
                We&apos;re happy to talk about our work and ideas.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex h-full flex-col rounded-3xl border border-nh-deep/5 bg-nh-cream p-8 shadow-soft transition-all duration-300 hover:-translate-y-2 hover:shadow-lift sm:col-span-2 lg:col-span-1">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-nh-gold">
                <Mail className="h-7 w-7" strokeWidth={1.75} />
              </div>
              <h2 className="mt-6 font-heading text-xl font-semibold text-nh-ink">✉️ Email Us</h2>
              <a
                href="mailto:nurturinghappiness2@gmail.com"
                data-testid="contact-email-link"
                className="mt-3 break-all text-[15px] font-medium text-nh-body transition-colors hover:text-nh-green"
              >
                nurturinghappiness2@gmail.com
              </a>
              <p className="mt-2 text-sm leading-relaxed text-nh-muted">
                Write to us anytime — we read every message.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="contact-form-section" className="py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <Reveal>
            <div className="rounded-[2rem] border border-nh-deep/5 bg-white p-7 shadow-lift sm:p-10 md:p-12">
              <h2 className="font-heading text-2xl font-bold text-nh-ink sm:text-3xl">
                Send Us a Message
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-nh-muted">
                Fill in the form below and we&apos;ll get back to you as soon as we can.
              </p>
              <form data-testid="contact-form" onSubmit={onSubmit} noValidate className="mt-8 space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="contact-name" className="text-sm font-semibold text-nh-ink">
                      Full Name
                    </Label>
                    <Input
                      id="contact-name"
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your full name"
                      className={fieldClass}
                    />
                    {errors.name && (
                      <p data-testid="contact-name-error" className="mt-1.5 text-xs font-medium text-red-600">
                        {errors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="contact-email" className="text-sm font-semibold text-nh-ink">
                      Email Address
                    </Label>
                    <Input
                      id="contact-email"
                      type="email"
                      data-testid="contact-email-input"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.com"
                      className={fieldClass}
                    />
                    {errors.email && (
                      <p data-testid="contact-email-error" className="mt-1.5 text-xs font-medium text-red-600">
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="contact-phone" className="text-sm font-semibold text-nh-ink">
                      Phone Number
                    </Label>
                    <Input
                      id="contact-phone"
                      type="tel"
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+91 ..."
                      className={fieldClass}
                    />
                    {errors.phone && (
                      <p data-testid="contact-phone-error" className="mt-1.5 text-xs font-medium text-red-600">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="contact-subject" className="text-sm font-semibold text-nh-ink">
                      Subject
                    </Label>
                    <Input
                      id="contact-subject"
                      data-testid="contact-subject-input"
                      value={form.subject}
                      onChange={update("subject")}
                      placeholder="What is this about?"
                      className={fieldClass}
                    />
                    {errors.subject && (
                      <p data-testid="contact-subject-error" className="mt-1.5 text-xs font-medium text-red-600">
                        {errors.subject}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact-message" className="text-sm font-semibold text-nh-ink">
                    Message
                  </Label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Share your question, idea or suggestion..."
                    rows={6}
                    className={fieldClass}
                  />
                  {errors.message && (
                    <p data-testid="contact-message-error" className="mt-1.5 text-xs font-medium text-red-600">
                      {errors.message}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={sending}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-nh-green px-9 py-3.5 text-[15px] font-semibold text-white shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-nh-deep hover:shadow-lift active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {sending ? "Sending..." : "Send Message"}
                </button>
                <p className="text-xs leading-relaxed text-nh-muted">
                  By submitting this form, you share your name, email, phone number and message with
                  Nurturing Happiness so we can respond to your enquiry. See our Privacy Policy for
                  details.
                </p>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
